import React, { useMemo } from 'react';
import {
  ActionList,
  ActionListItem,
  Box,
  Button,
  Dropdown,
  DropdownOverlay,
  Heading,
  SelectInput,
  Text,
  TextInput,
} from '@razorpay/blade/components';
import { useQuery } from 'react-query';
import api from 'api';
import { useFormWithProvider } from 'forms';
import { useWizardInfo } from 'components/WizardViews/providers/WizardInfoProvider';
import {
  FormWrapper,
  StepActionsWrapper,
  StepContentWrapper,
} from 'components/WizardViews/components';
import { AdminCreateLoanFormData } from './types';
import { PolicySummary } from './PolicySummary';
import { CACHE_TTL } from 'utils/cache';
import { BASIC_USER_LOAN_DATA_KEY, LOAN_CATEGORY_DETAIL_KEY } from './constants';
import { LoanCategory } from 'components/Settings/EmployeeLoansSettings/types';
import { formatMonthYear } from '../utils';

export const RepaymentDetails = () => {
  const { setCurrentStep } = useWizardInfo();
  const { formData, setFormData } = useFormWithProvider<AdminCreateLoanFormData>();
  const { data: basicLoanData } = useQuery({
    queryKey: [BASIC_USER_LOAN_DATA_KEY, formData.employee?.people_id],
    queryFn: () => api.loans.basicUserLoanData(formData.employee!.people_id),
    enabled: !!formData.employee,
  });

  const { data: categoryDetail } = useQuery<LoanCategory & { policy_document_url?: string }>({
    queryKey: [LOAN_CATEGORY_DETAIL_KEY, formData.loanCategoryId],
    queryFn: () => api.loans.getLoanCategory(formData.loanCategoryId!),
    enabled: !!formData.loanCategoryId,
    staleTime: CACHE_TTL.MEDIUM,
  });
  const categorySummary = categoryDetail?.policy_summary ?? null;

  const emiOptions = useMemo(() => {
    const base = basicLoanData?.last_payroll_month
      ? new Date(basicLoanData.last_payroll_month)
      : new Date();
    base.setDate(1);
    return Array.from({ length: 3 }, (_, i) => {
      const d = new Date(base);
      d.setMonth(d.getMonth() + i);
      const value = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-01`;
      return { label: formatMonthYear(value), value };
    });
  }, [basicLoanData?.last_payroll_month]);

  const canProceed = !!formData.tenure && !!formData.recoveryStartFrom;

  return (
    <Box display="flex" flex={1} overflow="hidden">
      <FormWrapper
        onSubmit={(e) => {
          e.preventDefault();
          setCurrentStep(3);
        }}
        style={{ flex: 1, minWidth: 0 }}>
        <StepContentWrapper noBgIllustration width="462px">
          <Box display="flex" flexDirection="column" gap="spacing.8">
            <Box>
              <Heading size="medium" weight="semibold">
                Repayment Details
              </Heading>
              <Text size="small" color="surface.text.gray.muted" marginTop="spacing.2">
                Choose repayment tenure and EMI start month
              </Text>
            </Box>
            <TextInput
              label="Repayment Tenure"
              placeholder="Enter tenure (1-24)"
              suffix="Months"
              type="number"
              value={String(formData.tenure)}
              onChange={({ value }) => {
                const num = Number(value);
                if (num >= 1 && num <= 24) {
                  setFormData({ ...formData, tenure: num });
                } else if (!value) {
                  setFormData({ ...formData, tenure: 0 });
                }
              }}
              helpText="Enter a value between 1 and 24 months"
            />
            <Dropdown selectionType="single">
              <SelectInput
                label="EMI Starts From"
                placeholder="Choose when EMI Deducts From"
                value={formData.recoveryStartFrom || undefined}
                onChange={({ values }) =>
                  setFormData({ ...formData, recoveryStartFrom: values[0] ?? '' })
                }
                helpText="Payroll month from which the EMI deduction starts"
              />
              <DropdownOverlay>
                <ActionList>
                  {emiOptions.map((opt) => (
                    <ActionListItem key={opt.value} title={opt.label} value={opt.value} />
                  ))}
                </ActionList>
              </DropdownOverlay>
            </Dropdown>
          </Box>
        </StepContentWrapper>

        <StepActionsWrapper width="462px">
          <Button variant="tertiary" onClick={() => setCurrentStep(1)}>
            Previous
          </Button>
          <Button type="submit" isDisabled={!canProceed}>
            Next
          </Button>
        </StepActionsWrapper>
      </FormWrapper>

      <PolicySummary
        policySummary={categorySummary}
        policyDocumentUrl={categoryDetail?.policy_document_url ?? null}
      />
    </Box>
  );
};
